/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { poService, csvService, supplierService } from '../services/api'
import { useAuth } from '../context/AuthContext'

function Dashboard() {
  const { user, isPanelok } = useAuth()
  const [stats, setStats] = useState({
    totalPOs: 0,
    draftPOs: 0,
    pendingPOs: 0,
    recentImports: []
  })
  const [recentPOs, setRecentPOs] = useState([])
  const [supplierMapping, setSupplierMapping] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    try {
      const [posRes, importsRes, mappingRes] = await Promise.all([
        poService.getAll({ limit: 10 }),
        csvService.getImports(5),
        supplierService.getMapping()
      ])

      const pos = posRes.purchaseOrders || []
      setRecentPOs(pos)
      setStats({
        totalPOs: posRes.total || pos.length,
        draftPOs: pos.filter(p => p.status === 'DRAFT').length,
        pendingPOs: pos.filter(p => p.status.includes('PENDING')).length,
        recentImports: importsRes.imports || []
      })
      setSupplierMapping(mappingRes.mappings || [])
    } catch (error) {
      console.error('Failed to load dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const classes = {
      'DRAFT': 'badge-draft',
      'PENDING_DIST_APPROVAL': 'badge-pending',
      'PENDING_PANELOK_REVIEW': 'badge-pending',
      'PENDING_PANELOK_APPROVAL': 'badge-pending',
      'APPROVED': 'badge-approved',
      'SENT_TO_SUPPLIER': 'badge-sent',
      'CONFIRMED': 'badge-confirmed',
      'COMPLETED': 'badge-completed',
      'CANCELLED': 'badge-cancelled'
    }
    return <span className={`badge ${classes[status] || ''}`}>{status.replace(/_/g, ' ')}</span>
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }} data-testid="loading-spinner">
        <div className="spinner" style={{ width: 40, height: 40 }}></div>
      </div>
    )
  }

  return (
    <div data-testid="pog-dashboard">
      <h1 style={{ marginBottom: 30 }} data-testid="dashboard-heading">Dashboard</h1>

      {/* Stats */}
      <div className="stats-grid" data-testid="stats-grid">
        <div className="stat-card" data-testid="stat-total-pos">
          <div className="stat-value">{stats.totalPOs}</div>
          <div className="stat-label">Total POs</div>
        </div>
        <div className="stat-card" data-testid="stat-draft-pos">
          <div className="stat-value">{stats.draftPOs}</div>
          <div className="stat-label">Drafts</div>
        </div>
        <div className="stat-card" data-testid="stat-pending-pos">
          <div className="stat-value">{stats.pendingPOs}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card" data-testid="stat-recent-imports">
          <div className="stat-value">{stats.recentImports.length}</div>
          <div className="stat-label">Recent Imports</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" data-testid="quick-actions-card">
        <div className="card-header">
          <h2>Quick Actions</h2>
        </div>
        <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
          <Link to="/csv-upload" className="btn btn-primary" data-testid="btn-upload-csv">
            Upload SketchUp CSV
          </Link>
          {isPanelok() && (
            <Link to="/manual-po" className="btn btn-secondary" data-testid="btn-create-manual-po">
              Create Manual PO
            </Link>
          )}
          <Link to="/purchase-orders" className="btn btn-outline" data-testid="btn-view-all-pos">
            View All POs
          </Link>
        </div>
      </div>

      {/* Supplier Mapping */}
      <div className="card" data-testid="supplier-mapping-card">
        <div className="card-header">
          <h2>Supplier Mapping (1-8)</h2>
          <Link to="/suppliers" className="btn btn-outline" style={{ fontSize: '0.85rem' }} data-testid="btn-manage-suppliers">
            Manage
          </Link>
        </div>
        <div className="supplier-grid">
          {supplierMapping.map(s => (
            <div key={s.supplier_number} className="supplier-card" data-testid={`supplier-card-${s.supplier_number}`}>
              <div className="supplier-number">{s.supplier_number}</div>
              <div className="supplier-info">
                <div className="supplier-name">{s.name || 'Not assigned'}</div>
                <div className="supplier-lead">
                  {s.lead_time_days ? `${s.lead_time_days} days lead time` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent POs */}
      <div className="card" data-testid="recent-pos-card">
        <div className="card-header">
          <h2>Recent Purchase Orders</h2>
          <Link to="/purchase-orders" className="btn btn-outline" style={{ fontSize: '0.85rem' }}>
            View All
          </Link>
        </div>
        {recentPOs.length === 0 ? (
          <p style={{ color: '#666' }} data-testid="no-pos-message">No purchase orders yet.</p>
        ) : (
          <div className="table-container">
            <table data-testid="recent-pos-table">
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Job</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentPOs.map(po => (
                  <tr key={po.id} data-testid={`po-row-${po.id}`}>
                    <td>
                      <Link to={`/purchase-orders/${po.id}`} style={{ color: '#1a5f7a', fontWeight: 500 }} data-testid={`po-link-${po.id}`}>
                        {po.po_number}
                      </Link>
                    </td>
                    <td>{po.job_number || '-'}</td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 24, height: 24,
                          background: '#1a5f7a', color: 'white',
                          borderRadius: '50%', fontSize: '0.75rem',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          {po.supplier_number}
                        </span>
                        {po.supplier_name_snapshot}
                      </span>
                    </td>
                    <td>{getStatusBadge(po.status)}</td>
                    <td>${parseFloat(po.total_amount || 0).toFixed(2)}</td>
                    <td>{new Date(po.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default Dashboard
